import React, { useState } from 'react';

import {
  cards,
  careers,
  databases,
  heroStats,
  logoCategories,
  other,
  programmings,
  systemNodes,
  tools,
} from './portfolioData';
import { useTypewriter } from './hooks/useTypewriter';
import { SectionTitle } from './components/SectionTitle';
import { ProjectCard } from './components/ProjectCard';
import { CareerItem } from './components/CareerItem';
import { SkillCategory } from './components/SkillCategory';

export default function Album() {
  const [menuState, setMenuState] = useState({
    competency: false,
    skill: false,
    tool: false,
    db: false,
    other: false,
  });

  const handleClickMenu = (key) => {
    setMenuState((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const handleBrandClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (window.location.hash) {
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }
  };

  const typedName = useTypewriter('Punyapat Sessomboon');

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-[#7df9ff]/[0.24] bg-[#0f1f3a]/[0.78] shadow-[0_14px_40px_rgba(0,0,0,0.2)] backdrop-blur-[18px]">
        <div className="flex min-h-[56px] items-center px-4 sm:min-h-[64px] sm:px-6">
          <div
            className="cursor-pointer select-none truncate font-mono text-base font-bold tracking-[0.01em] transition-[text-shadow] duration-200 hover:[text-shadow:0_0_10px_rgba(0,243,255,0.45)]"
            onClick={handleBrandClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleBrandClick();
              }
            }}
          >
            <span className="text-[#00ff41]">perth</span>
            <span className="text-[#a4b4d2]">@</span>
            <span className="text-[#00f3ff]">portfolio</span>
            <span className="text-[#a4b4d2]">:~$</span>
            <span className="pulse-dot" title="online"></span>
          </div>
          <nav className="ml-auto hidden items-center sm:flex">
            <a
              href="#projects"
              className="ml-6 font-mono text-[0.8rem] text-slate-300 no-underline transition-all duration-200 hover:text-[#00f3ff] hover:[text-shadow:0_0_8px_rgba(0,243,255,0.6)]"
            >
              /projects
            </a>
            <a
              href="#stack"
              className="ml-6 font-mono text-[0.8rem] text-slate-300 no-underline transition-all duration-200 hover:text-[#00f3ff] hover:[text-shadow:0_0_8px_rgba(0,243,255,0.6)]"
            >
              /stack
            </a>
            <a
              href="#career"
              className="ml-6 font-mono text-[0.8rem] text-slate-300 no-underline transition-all duration-200 hover:text-[#00f3ff] hover:[text-shadow:0_0_8px_rgba(0,243,255,0.6)]"
            >
              /career
            </a>
            <a
              href="#contact"
              className="ml-6 font-mono text-[0.8rem] text-slate-300 no-underline transition-all duration-200 hover:text-[#00f3ff] hover:[text-shadow:0_0_8px_rgba(0,243,255,0.6)]"
            >
              /contact
            </a>
          </nav>
        </div>
      </header>

      <main className="relative isolate">
        <div className="relative flex min-h-0 items-center overflow-hidden pb-12 pt-16 text-[#e8effd] md:min-h-[calc(100vh-64px)] md:pb-16 md:pt-20">
          <div className="mx-auto w-full max-w-[1480px] px-4 sm:px-8">
            <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-12">
              <div className="md:col-span-7">
                <p className="mb-4 font-mono text-[0.88rem] text-[#00f3ff]">
                  {'// technical director / product-minded builder'}
                </p>
                <h1 className="m-0 min-h-[1.05em] break-words font-mono text-[2.12rem] font-extrabold leading-[1.08] text-white [text-shadow:0_0_26px_rgba(0,243,255,0.28)] sm:text-[3.75rem] sm:leading-[0.96]">
                  <span className="sr-only">Punyapat Sessomboon</span>
                  <span aria-hidden="true">{typedName}</span>
                </h1>
                <p className="mt-6 max-w-[840px] text-[1.02rem] leading-[1.7] text-slate-200 sm:text-[1.24rem]">
                  I build and lead production systems across mobile, cloud
                  infrastructure, data platforms, and AI-assisted engineering.
                  My work sits between architecture, execution, product
                  judgment, and team direction.
                </p>
                <div className="mt-8 grid max-w-[840px] grid-cols-1 gap-4 sm:grid-cols-3">
                  {heroStats.map((stat) => (
                    <div
                      className="rounded-lg border border-[#00f3ff]/[0.22] bg-[linear-gradient(180deg,rgba(24,43,73,0.78),rgba(14,26,48,0.82))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                      key={stat.label}
                    >
                      <div className="mb-4 font-mono text-xl font-extrabold text-white">
                        {stat.value}
                      </div>
                      <div className="font-mono text-[0.72rem] uppercase text-slate-300">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a
                    href="https://drive.google.com/file/d/1E3uecxoZEZTSj7nLJTce4MezGUQbZ5wf/view"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-[2px] bg-[#00f3ff] px-4 py-1.5 font-mono text-sm font-medium uppercase tracking-[0.06em] text-black/90 no-underline shadow-[0_0_12px_rgba(0,243,255,0.35)] transition-all duration-300 hover:bg-[#00f3ff]/90 hover:shadow-[0_0_24px_rgba(0,243,255,0.6)]"
                  >
                    <svg
                      className="h-5 w-5 fill-current"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" />
                    </svg>
                    Resume
                  </a>
                  <a
                    href="https://perth.medium.com"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-[2px] border border-[#00f3ff]/50 px-4 py-1.5 font-mono text-sm font-medium uppercase tracking-[0.06em] text-[#00f3ff] no-underline transition-all duration-300 hover:border-[#00f3ff] hover:bg-[#00f3ff]/[0.06] hover:shadow-[0_0_16px_rgba(0,243,255,0.3)]"
                  >
                    <svg
                      className="h-5 w-5 fill-current"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                    Medium
                  </a>
                  <a
                    href="https://github.com/perthcpe23"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-[2px] border border-[#00f3ff]/50 px-4 py-1.5 font-mono text-sm font-medium uppercase tracking-[0.06em] text-[#00f3ff] no-underline transition-all duration-300 hover:border-[#00f3ff] hover:bg-[#00f3ff]/[0.06] hover:shadow-[0_0_16px_rgba(0,243,255,0.3)]"
                  >
                    <svg
                      className="h-5 w-5 fill-current"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M12 .3a12 12 0 0 0-3.8 23.4c.6.1.8-.3.8-.6v-2c-3.3.7-4-1.6-4-1.6-.6-1.4-1.4-1.8-1.4-1.8-1-.7.1-.7.1-.7 1.2.1 1.8 1.2 1.8 1.2 1 1.8 2.8 1.3 3.5 1 .1-.8.4-1.3.7-1.6-2.7-.3-5.5-1.3-5.5-5.9 0-1.3.5-2.4 1.2-3.2-.1-.3-.5-1.5.1-3.2 0 0 1-.3 3.3 1.2a11.5 11.5 0 0 1 6 0c2.3-1.5 3.3-1.2 3.3-1.2.6 1.7.2 2.9.1 3.2.8.8 1.2 1.9 1.2 3.2 0 4.6-2.8 5.6-5.5 5.9.4.4.8 1.1.8 2.2v3.3c0 .3.2.7.8.6A12 12 0 0 0 12 .3" />
                    </svg>
                    Github
                  </a>
                  <a
                    href="https://www.linkedin.com/in/punyapat-s/"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-[2px] border border-[#00f3ff]/50 px-4 py-1.5 font-mono text-sm font-medium uppercase tracking-[0.06em] text-[#00f3ff] no-underline transition-all duration-300 hover:border-[#00f3ff] hover:bg-[#00f3ff]/[0.06] hover:shadow-[0_0_16px_rgba(0,243,255,0.3)]"
                  >
                    <svg
                      className="h-5 w-5 fill-current"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                    </svg>
                    Linkedin
                  </a>
                </div>
              </div>

              <div className="md:col-span-5">
                <div className="relative w-full max-w-full overflow-hidden rounded-lg border border-[#00f3ff]/[0.34] bg-[linear-gradient(145deg,rgba(24,43,75,0.88),rgba(13,25,46,0.92))] shadow-[0_24px_80px_rgba(0,0,0,0.28),0_0_38px_rgba(0,243,255,0.16)] md:[transform:perspective(900px)_rotateX(2deg)_rotateY(-5deg)]">
                  <div className="flex h-[42px] items-center gap-2 border-b border-[#00f3ff]/[0.22] bg-slate-800/[0.72] px-4">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#00f3ff] shadow-[0_0_12px_rgba(0,243,255,0.7)]"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-[#ff9500] shadow-[0_0_12px_rgba(255,149,0,0.65)]"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-[#00ff41] shadow-[0_0_12px_rgba(0,255,65,0.62)]"></span>
                  </div>
                  <div className="p-4 font-mono sm:p-6">
                    <p className="mb-3 break-words font-mono text-[0.82rem] text-slate-300">
                      <span className="text-[#00ff41]">perth@portfolio</span>
                      :~$ deploy --scope leadership --mode production
                    </p>
                    <p className="mb-3 break-words font-mono text-[0.82rem] text-slate-300">
                      resolving strategy graph... done
                    </p>
                    <p className="mb-3 break-words font-mono text-[0.82rem] text-slate-300">
                      linking teams, systems, roadmap... done
                    </p>
                    <p className="mb-3 break-words font-mono text-[0.82rem] text-slate-300">
                      observability: product, delivery, reliability
                    </p>
                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {systemNodes.map((node) => (
                        <div
                          className="min-h-[72px] rounded-md border border-[#00f3ff]/[0.2] bg-slate-800/[0.58] p-3"
                          key={node.label}
                        >
                          <div className="text-xs uppercase tracking-[0.08em] text-slate-400">
                            {node.label}
                          </div>
                          <div className="mt-1.5 text-sm font-bold text-white">
                            {node.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section
          className="mx-auto w-full max-w-[1480px] px-4 py-12 sm:px-8 sm:py-16"
          id="career"
        >
          <SectionTitle index={1}>Career Timeline</SectionTitle>
          <nav>
            {careers.map((career) => (
              <CareerItem
                key={career.code}
                career={career}
                isOpen={Boolean(menuState[career.code])}
                onToggle={() => handleClickMenu(career.code)}
              />
            ))}
          </nav>
        </section>

        <section
          className="mx-auto w-full max-w-[1480px] px-4 py-12 sm:px-8 sm:py-16"
          id="stack"
        >
          <SectionTitle index={2}>Tech Stack</SectionTitle>
          <div className="space-y-8 rounded-lg border border-[#00f3ff]/[0.2] bg-[#142542]/[0.68] p-4 shadow-[0_12px_40px_rgba(0,0,0,0.18)] sm:p-6">
            {logoCategories.map((category) => (
              <div key={category.title}>
                <div className="mb-4 flex items-center gap-3">
                  <h3 className="m-0 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[#00f3ff] sm:text-sm">
                    {'// '}
                    {category.title}
                  </h3>
                  <div className="h-px flex-grow bg-[linear-gradient(90deg,rgba(0,243,255,0.28),transparent)]" />
                </div>
                <div className="grid grid-cols-4 gap-x-4 gap-y-6 sm:grid-cols-6 md:grid-cols-12">
                  {category.items.map((e) => (
                    <div
                      key={e.alt}
                      className="flex flex-col items-center text-center"
                    >
                      <img
                        className="aspect-square h-auto w-full rounded-3xl object-contain transition-[filter,transform] duration-300"
                        src={e.src}
                        alt={e.alt}
                        title={e.alt}
                      />
                      <span className="mt-2 w-full truncate font-mono text-[0.7rem] text-[#e8effd] sm:text-xs">
                        {e.alt}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section
          className="mx-auto w-full max-w-[1480px] px-4 py-12 sm:px-8 sm:py-16"
          id="skills"
        >
          <SectionTitle index={3}>Additional Skills &amp; Tools</SectionTitle>
          <nav>
            <SkillCategory
              title="Mainly used languages"
              icon={
                <svg
                  className="h-6 w-6 fill-current text-[#00f3ff]"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M11.99 18.54l-7.37-5.73L3 14.07l9 7 9-7-1.63-1.27-7.38 5.74zM12 16l7.36-5.73L21 9l-9-7-9 7 1.63 1.27L12 16z" />
                </svg>
              }
              items={programmings}
              isOpen={Boolean(menuState.skill)}
              onToggle={() => handleClickMenu('skill')}
              defaultColor="#00f3ff"
            />
            <SkillCategory
              title="Databases, search index, cache"
              icon={
                <svg
                  className="h-6 w-6 fill-current text-[#bc13fe]"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M20 13H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1zm0-10H3c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h17c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1z" />
                </svg>
              }
              items={databases}
              isOpen={Boolean(menuState.db)}
              onToggle={() => handleClickMenu('db')}
              defaultColor="#bc13fe"
            />
            <SkillCategory
              title="Tools"
              icon={
                <svg
                  className="h-6 w-6 fill-current text-[#ff9500]"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" />
                </svg>
              }
              items={tools}
              isOpen={Boolean(menuState.tool)}
              onToggle={() => handleClickMenu('tool')}
              defaultColor="rgb(255 149 0)"
            />
            <SkillCategory
              title="Skills"
              icon={
                <svg
                  className="h-6 w-6 fill-current text-[#00ff41]"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                </svg>
              }
              items={other}
              isOpen={Boolean(menuState.other)}
              onToggle={() => handleClickMenu('other')}
              defaultColor="#00ff41"
            />
          </nav>
        </section>

        <section
          className="mx-auto w-full max-w-[1480px] px-4 py-12 sm:px-8 sm:py-16"
          id="projects"
        >
          <SectionTitle index={4}>Projects</SectionTitle>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {cards
              .filter((card) => card.visible !== false)
              .map((card) => (
                <ProjectCard key={card.title} card={card} />
              ))}
          </div>
        </section>

        <section
          className="mx-auto w-full max-w-[1480px] px-4 py-12 sm:px-8 sm:py-16"
          id="ai"
        >
          <SectionTitle index={5}>AI-Augmented Engineering</SectionTitle>
          <div className="relative overflow-hidden rounded-lg border border-[#00f3ff]/[0.32] bg-[linear-gradient(135deg,rgba(24,43,75,0.82),rgba(14,27,49,0.9))] p-5 shadow-[0_18px_60px_rgba(0,0,0,0.18)] sm:p-8">
            <h3 className="mb-2 font-mono text-2xl font-bold text-[#00f3ff]">
              Building Production Software with Agents and Judgment
            </h3>
            <p className="mb-4 text-[1.1rem] text-[#e8effd]">
              I use coding agents like <b>Claude Code</b>, <b>Codex</b> and{' '}
              <b>Antigravity CLI</b> to compress implementation cycles while
              keeping the hard parts human: architecture, product tradeoffs,
              reliability, maintainability, and technical direction.
            </p>
            <p className="my-6 border-l-4 border-[#00f3ff] pl-5 italic text-[#b8c7e4]">
              "I believe AI should be paired with a deep understanding of
              software development fundamentals."
            </p>
            <p className="text-sm leading-[1.6] text-[#a4b4d2]">
              My engineering philosophy is to use AI as leverage, not as a
              substitute for ownership. The goal is faster iteration with
              stronger review discipline, clearer system boundaries, and
              production software that a team can keep evolving.
            </p>
          </div>
        </section>
      </main>

      <footer
        className="border-t border-[#00f3ff]/[0.2] bg-[linear-gradient(180deg,rgba(15,31,58,0.4),#081223)] p-12"
        id="contact"
      >
        <h3 className="mb-2 text-center font-mono text-xl font-bold text-[#e8effd]">
          <span className="text-[#00f3ff]">06.</span> Contact
        </h3>
        <div className="text-center text-[#a4b4d2]">
          <div className="text-base text-[#e8effd]">
            Punyapat Sessomboon (Perth)
          </div>
          <div className="mb-1.5 text-base">
            ปุญญพัฒน์ เศรษฐ์สมบูรณ์ (เพิร์ท)
          </div>
          <a href="tel:+6687-717-0741" className="text-[#00f3ff]">
            (+66) 87-717-0741
          </a>
          <br />
          <a href="mailto:perth.s28@gmail.com" className="text-[#00f3ff]">
            perth.s28@gmail.com
          </a>
        </div>
      </footer>
    </>
  );
}
